import type { RosterMember } from '../useHouseholdRoster';
import { textOnMemberColor } from '../../../theme/memberColor';

export interface CreatorFilterChipsProps {
  creators: RosterMember[];
  activeCreatorId: string | null;
  onChange: (creatorId: string | null) => void;
}

/**
 * "Added by" filter chips (owner ask, 2026-08-04: "if Faye only wants to see the recipes
 * she's been adding, that's really easy"). One chip per household member who has *at least
 * one* recipe in the box — not the full roster, so an empty-handed member never clutters the
 * row. Colour is the member's own chosen hex rendered inline, same as check-in's `Avatar`
 * (`docs/DECISIONS.md`, 2026-08-01, "member colour truth") — never a fixed `bg-member-*`
 * token, which would misrender the moment two members' names don't happen to be Faye/Dan/
 * Ezry/Kora or a roster position shifts.
 */
export function CreatorFilterChips({
  creators,
  activeCreatorId,
  onChange,
}: CreatorFilterChipsProps): JSX.Element {
  return (
    <div className="flex gap-2 overflow-x-auto px-4" data-testid="recipe-creator-filter">
      {creators.map((member) => {
        const active = activeCreatorId === member.id;
        const style = active
          ? { backgroundColor: member.color, color: textOnMemberColor(member.color) }
          : undefined;
        return (
          <button
            key={member.id}
            type="button"
            data-testid="recipe-creator-chip"
            onClick={() => onChange(active ? null : member.id)}
            aria-pressed={active}
            style={style}
            className={`flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold ease-app ${
              active ? '' : 'border border-line text-fg-2'
            }`}
          >
            <span aria-hidden="true">
              {member.avatarEmoji ?? member.name.charAt(0).toUpperCase()}
            </span>
            {member.name}
          </button>
        );
      })}
    </div>
  );
}
